import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Pagination,
	PagingConfig,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { FunctionariesListState } from './FunctionariesList.store';
import { useSnapshot } from 'valtio';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useAuth } from '../../../hooks/useAuth';
import {
	bulkUploadFunctionaries,
	getPositionAssignmentUsers,
} from '../../../service/MySrcmService';
import { downloadTemplateForBulkUploadFunctionaries } from '../../../service/VolunteerApplicationService';
import { PositionAssignmentUser } from '../../../shared/MySrcm';
import { useBatchId } from '../../../hooks/useBatchId';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE } from '../../../shared/Constants';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import {
	getAndDownloadFile,
	getSignedUrl,
	uploadFilesToCloud,
} from '../../../service/ExamModuleService';
import { downloadFile } from '../../../shared/Utils';
import { SignedUrlDetail } from '../../../shared/ExamModuleTypes';
import { UploadFile } from 'antd';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import { VIEW_FUNCTIONARIES } from '../../../shared/Routes';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import { getRoles } from '../../../service/RoleBasedService';
import { PositionAssignmentFilters } from './FilterFunctionaries';

export interface PositionAssignmentFunctionaryList {
	functionary: PositionAssignmentUser[];
	pagination: Pagination;
}

export const useFunctionariesList = () => {
	const { t } = useTranslation();
	const [functionaryList, setFunctionaryList] =
		useState<PositionAssignmentFunctionaryList>({
			functionary: [],
			pagination: { totalNoOfRecords: 0 },
		});
	const [isFilterTextApplied, setIsFilterTextApplied] =
		useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [isApiFetched, setIsApiFetched] = useState<boolean>(false);
	const { initiated, srcmId } = useAuth();
	const { navigateToRoute, getRouteWithParams, queryParams } = useBatchId();
	const { infoPopUpAction } = useInfoPopup();
	const navigate = useNavigate();
	const filterStateSnap = useSnapshot(FunctionariesListState);
	const [paging, setPaging] = useState<PagingConfig>(() => {
		return { ...filterStateSnap.paging } as PagingConfig;
	});
	const [filter, setFilter] = useState<PositionAssignmentFilters>(() => {
		return filterStateSnap.filterBy ? { ...filterStateSnap.filterBy } : {};
	});
	const { showToastMessage } = useToastMessage();

	const functionarySrcmIdParam = useMemo(() => {
		const functionarySIdParam = queryParams.get('functionarySrcmIdParam');
		if (typeof functionarySIdParam !== 'string') {
			return '-1';
		}

		return functionarySIdParam;
	}, [queryParams]);

	const navigateWithFunctionarySrcmId = useCallback(
		(route: string, functionarySIdParam?: string, params?: NavigateOptions) => {
			const proposerSrcmIdToUse = functionarySIdParam
				? `${functionarySIdParam}`
				: functionarySrcmIdParam;
			queryParams.set('functionarySrcmIdParam', proposerSrcmIdToUse);
			navigateToRoute(route, params);
		},
		[navigateToRoute, functionarySrcmIdParam, queryParams]
	);

	const navigateWithoutSrcmId = useCallback(
		(route: string, params?: NavigateOptions) => {
			navigate(`${getRouteWithParams(route, true)}`, params);
		},
		[getRouteWithParams, navigate]
	);

	const onPageChange = useCallback((pageNumber: number, pageSize: number) => {
		FunctionariesListState.paging = { pageNo: pageNumber, pageSize };
		setPaging({ pageNo: pageNumber, pageSize });
	}, []);

	const fetchFunctionariesList = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			filters: PositionAssignmentFilters
		) => {
			if (initiated && srcmId) {
				setLoading(true);
				try {
					const response = await getPositionAssignmentUsers({
						...filters,
						page: pageNumber,
						page_size: pageSize,
					});
					const totalPages = Math.ceil(
						response.count / (pageSize ?? DEFAULT_PAGE_SIZE)
					);

					if (totalPages > 0 && pageNumber > totalPages) {
						onPageChange(totalPages, pageSize);
					} else {
						setFunctionaryList({
							functionary: response.results,
							pagination: {
								totalNoOfRecords: response.count,
								totalNoOfPages: totalPages,
								pageNo: pageNumber,
								pageSize,
							},
						});
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
					setIsApiFetched(true);
				}
			}
		},
		[initiated, onPageChange, setLoading, showToastMessage, srcmId]
	);

	const onFilterChange = useCallback(
		(filters: PositionAssignmentFilters) => {
			const hasFilters = Object.values(filters).some((val) => val);
			setIsFilterTextApplied(hasFilters);
			onPageChange(1, paging.pageSize ?? PAGE_SIZE);
			setFilter(filters);
		},
		[onPageChange, paging.pageSize]
	);

	const downloadTemplateForBulkFunctionaries = useCallback(
		async (functionaryType: string) => {
			if (srcmId) {
				setLoading(true);
				try {
					const downloadResponse =
						await downloadTemplateForBulkUploadFunctionaries({
							functionarySrcmId: srcmId,
							functionaryType,
						});
					if (downloadResponse.errorMessage) {
						showToastMessage('error', downloadResponse.errorMessage);
						return;
					}
					if (downloadResponse.csvReportSignedUrl) {
						let fileName = 'Bulkupload_Template';
						fileName += '.xlsx';
						const blobResponse = await getAndDownloadFile(
							downloadResponse.csvReportSignedUrl
						);
						if (blobResponse) {
							downloadFile(blobResponse, fileName);
						}
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[setLoading, showToastMessage, srcmId]
	);

	const fetchRoleId = useCallback(
		async (roleCode: string) => {
			const resp = await getRoles({
				loggedInUserSrcmId: srcmId ?? '',
				selectedRoleCode: roleCode,
				filters: { roleCode },
				pageIndex: 0,
				pageSize: 1,
			});
			return resp.roles?.[0]?.id;
		},
		[srcmId]
	);

	const bulkUploadDataFunctionaries = useCallback(
		async (
			signedURLDetail: Array<SignedUrlDetail>,
			file: UploadFile<Blob>,
			fileExtension: string | undefined,
			functionaryType: string
		) => {
			if (!srcmId) {
				return;
			}
			setLoading(true);
			try {
				const signedURLResponse = await getSignedUrl({
					functionarySrcmId: srcmId,
					signedUrlDetail: signedURLDetail,
				});
				if (
					!signedURLResponse.signedUrlDetail.length ||
					signedURLResponse.signedUrlDetail.length === 0
				) {
					showToastMessage('error', 'Unknown error occurred');
					return;
				}
				// Uploading file to Cloud
				await uploadFilesToCloud(
					file,
					fileExtension,
					signedURLResponse.signedUrlDetail[0].signedUrl
				);
				if (!signedURLResponse.signedUrlDetail[0].objectPath) {
					showToastMessage('error', 'Unknown error occurred');
					return;
				}
				const cloudFileName =
					signedURLResponse.signedUrlDetail[0].objectPath.split('.com/')[1];
				const roleId = await fetchRoleId(functionaryType);

				const uploadResponse = await bulkUploadFunctionaries({
					functionarySrcmId: srcmId,
					fileNameInCloudStorage: cloudFileName,
					roleId,
				});
				if (uploadResponse) {
					infoPopUpAction(
						'bulkUploadStarted',
						'bulkUploadInformation',
						() => {
							navigateWithoutSrcmId(VIEW_FUNCTIONARIES);
						},
						ConfirmTick
					);
				} else {
					showToastMessage('error', t('resultsUploadErrorMessage'));
				}
			} catch (e) {
				if (e instanceof AxiosError) {
					// eslint-disable-next-line
					showToastMessage('error', e?.response?.data?.message || e.message);
				} else if (e instanceof Error) {
					showToastMessage('error', e.message);
				} else {
					showToastMessage('error', 'Unknown error occurred');
				}
			} finally {
				setLoading(false);
			}
		},
		[
			setLoading,
			showToastMessage,
			srcmId,
			t,
			infoPopUpAction,
			navigateWithoutSrcmId,
			fetchRoleId,
		]
	);

	useEffect(() => {
		void fetchFunctionariesList(
			paging.pageNo ?? 1,
			paging.pageSize ?? PAGE_SIZE,
			filter
		);
	}, [fetchFunctionariesList, filter, paging.pageNo, paging.pageSize]);

	return {
		functionaryList,
		loading,
		isApiFetched,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		functionarySrcmIdParam,
		navigateWithFunctionarySrcmId,
		isFilterTextApplied,
		setIsFilterTextApplied,
		downloadTemplateForBulkFunctionaries,
		bulkUploadDataFunctionaries,
	};
};
